import { create } from 'zustand';
import { Discussion, DiscussionComment } from '@services/discussionService';

interface DiscussionState {
    // List view state
    discussions: Discussion[];
    totalPages: number;
    currentPage: number;
    selectedType: string;
    listLoading: boolean;
    hasMore: boolean;

    // Detail view state
    selectedDiscussion: Discussion | null;
    detailLoading: boolean;

    // Actions for list
    setDiscussions: (discussions: Discussion[], totalPages: number) => void;
    appendDiscussions: (discussions: Discussion[], hasMore: boolean) => void;
    setListLoading: (loading: boolean) => void;
    setCurrentPage: (page: number) => void;
    setSelectedType: (type: string) => void;

    // Actions for detail
    setSelectedDiscussion: (discussion: Discussion | null) => void;
    setDetailLoading: (loading: boolean) => void;

    // Optimistic update actions
    updateDiscussionVote: (
        discussionId: string,
        userId: string,
        voteType: 'up' | 'down',
        upvoteCount: number,
        downvoteCount: number
    ) => void;
    updateDiscussionHeart: (discussionId: string, adminHearted: boolean) => void;
    updateDiscussionPin: (discussionId: string, pinned: boolean) => void;
    updateDiscussionContent: (discussionId: string, type: string, content: string) => void;
    removeDiscussion: (discussionId: string) => void;

    // Comment optimistic updates
    addComment: (discussionId: string, comment: DiscussionComment) => void;
    updateCommentVote: (
        discussionId: string,
        commentId: string,
        userId: string,
        voteType: 'up' | 'down',
        upvoteCount: number,
        downvoteCount: number
    ) => void;
    updateCommentContent: (discussionId: string, commentId: string, content: string) => void;
    removeComment: (discussionId: string, commentId: string) => void;

    // Refresh comments (for bot responses arriving in background)
    refreshComments: (discussionId: string, comments: DiscussionComment[]) => void;

    // Reset
    reset: () => void;
}

const initialState = {
    discussions: [],
    totalPages: 1,
    currentPage: 1,
    selectedType: 'all',
    listLoading: false,
    hasMore: false,
    selectedDiscussion: null,
    detailLoading: false,
};

export const useDiscussionStore = create<DiscussionState>()((set, get) => ({
    ...initialState,

    // List actions
    setDiscussions: (discussions, totalPages) =>
        set({ discussions, totalPages, hasMore: totalPages > 1 }),

    appendDiscussions: (newDiscussions, hasMore) =>
        set((state) => ({
            discussions: [...state.discussions, ...newDiscussions],
            hasMore,
        })),

    setListLoading: (listLoading) => set({ listLoading }),

    setCurrentPage: (currentPage) => set({ currentPage }),

    setSelectedType: (selectedType) => set({ selectedType, currentPage: 1, discussions: [], hasMore: false }),

    // Detail actions
    setSelectedDiscussion: (selectedDiscussion) => set({ selectedDiscussion }),

    setDetailLoading: (detailLoading) => set({ detailLoading }),

    // Optimistic vote update for discussions
    updateDiscussionVote: (discussionId, userId, voteType, upvoteCount, downvoteCount) => {
        set((state) => {
            // Helper to update vote arrays
            const updateVotes = (discussion: Discussion): Discussion => {
                const upvotes = discussion.upvotes ? [...discussion.upvotes] : [];
                const downvotes = discussion.downvotes ? [...discussion.downvotes] : [];

                // Remove from both arrays first
                const upIndex = upvotes.indexOf(userId);
                const downIndex = downvotes.indexOf(userId);
                if (upIndex > -1) upvotes.splice(upIndex, 1);
                if (downIndex > -1) downvotes.splice(downIndex, 1);

                // Add to appropriate array based on vote type
                if (voteType === 'up' && upIndex === -1) {
                    upvotes.push(userId);
                } else if (voteType === 'down' && downIndex === -1) {
                    downvotes.push(userId);
                }

                return {
                    ...discussion,
                    upvotes,
                    downvotes,
                    upvote_count: upvoteCount,
                    downvote_count: downvoteCount,
                };
            };

            // Update in list
            const discussions = state.discussions.map((d) =>
                d.id === discussionId ? updateVotes(d) : d
            );

            // Update selected discussion if it matches
            const selectedDiscussion =
                state.selectedDiscussion?.id === discussionId
                    ? updateVotes(state.selectedDiscussion)
                    : state.selectedDiscussion;

            return { discussions, selectedDiscussion };
        });
    },

    // Admin heart toggle
    updateDiscussionHeart: (discussionId, adminHearted) => {
        set((state) => {
            const discussions = state.discussions.map((d) =>
                d.id === discussionId ? { ...d, admin_hearted: adminHearted } : d
            );

            const selectedDiscussion =
                state.selectedDiscussion?.id === discussionId
                    ? { ...state.selectedDiscussion, admin_hearted: adminHearted }
                    : state.selectedDiscussion;

            return { discussions, selectedDiscussion };
        });
    },

    // Pin toggle
    updateDiscussionPin: (discussionId, pinned) => {
        set((state) => {
            const discussions = state.discussions.map((d) =>
                d.id === discussionId ? { ...d, pinned } : d
            );

            const selectedDiscussion =
                state.selectedDiscussion?.id === discussionId
                    ? { ...state.selectedDiscussion, pinned }
                    : state.selectedDiscussion;

            return { discussions, selectedDiscussion };
        });
    },

    // Update discussion content (after edit)
    updateDiscussionContent: (discussionId, type, content) => {
        set((state) => {
            const updatedType = type as Discussion['type'];
            const discussions = state.discussions.map((d) =>
                d.id === discussionId ? { ...d, type: updatedType, content } : d
            );

            const selectedDiscussion =
                state.selectedDiscussion?.id === discussionId
                    ? { ...state.selectedDiscussion, type: updatedType, content }
                    : state.selectedDiscussion;

            return { discussions, selectedDiscussion };
        });
    },

    // Remove discussion from list
    removeDiscussion: (discussionId) => {
        set((state) => ({
            discussions: state.discussions.filter((d) => d.id !== discussionId),
            selectedDiscussion:
                state.selectedDiscussion?.id === discussionId
                    ? null
                    : state.selectedDiscussion,
        }));
    },

    // Add new comment
    addComment: (discussionId, comment) => {
        set((state) => {
            if (state.selectedDiscussion?.id !== discussionId) return state;

            const comments = [...(state.selectedDiscussion.comments || []), comment];
            return {
                selectedDiscussion: {
                    ...state.selectedDiscussion,
                    comments,
                    comment_count: comments.length,
                },
                // Also update comment count in list
                discussions: state.discussions.map((d) =>
                    d.id === discussionId
                        ? { ...d, comment_count: d.comment_count + 1 }
                        : d
                ),
            };
        });
    },

    // Optimistic vote update for comments
    updateCommentVote: (discussionId, commentId, userId, voteType, upvoteCount, downvoteCount) => {
        set((state) => {
            if (state.selectedDiscussion?.id !== discussionId) return state;

            const comments = (state.selectedDiscussion.comments || []).map((c) => {
                if (c.comment_id !== commentId) return c;

                const upvotes = c.upvotes ? [...c.upvotes] : [];
                const downvotes = c.downvotes ? [...c.downvotes] : [];

                // Remove from both arrays first
                const upIndex = upvotes.indexOf(userId);
                const downIndex = downvotes.indexOf(userId);
                if (upIndex > -1) upvotes.splice(upIndex, 1);
                if (downIndex > -1) downvotes.splice(downIndex, 1);

                // Add to appropriate array
                if (voteType === 'up' && upIndex === -1) {
                    upvotes.push(userId);
                } else if (voteType === 'down' && downIndex === -1) {
                    downvotes.push(userId);
                }

                return {
                    ...c,
                    upvotes,
                    downvotes,
                    upvote_count: upvoteCount,
                    downvote_count: downvoteCount,
                };
            });

            return {
                selectedDiscussion: {
                    ...state.selectedDiscussion,
                    comments,
                },
            };
        });
    },

    // Update comment content
    updateCommentContent: (discussionId, commentId, content) => {
        set((state) => {
            if (state.selectedDiscussion?.id !== discussionId) return state;

            const comments = (state.selectedDiscussion.comments || []).map((c) =>
                c.comment_id === commentId ? { ...c, content } : c
            );

            return {
                selectedDiscussion: {
                    ...state.selectedDiscussion,
                    comments,
                },
            };
        });
    },

    // Remove comment
    removeComment: (discussionId, commentId) => {
        set((state) => {
            if (state.selectedDiscussion?.id !== discussionId) return state;

            const comments = (state.selectedDiscussion.comments || []).filter(
                (c) => c.comment_id !== commentId
            );

            return {
                selectedDiscussion: {
                    ...state.selectedDiscussion,
                    comments,
                    comment_count: comments.length,
                },
                discussions: state.discussions.map((d) =>
                    d.id === discussionId
                        ? { ...d, comment_count: Math.max(0, d.comment_count - 1) }
                        : d
                ),
            };
        });
    },

    // Refresh comments from server (for bot responses)
    refreshComments: (discussionId, comments) => {
        set((state) => {
            if (state.selectedDiscussion?.id !== discussionId) return state;

            return {
                selectedDiscussion: {
                    ...state.selectedDiscussion,
                    comments,
                    comment_count: comments.length,
                },
                // Also update comment count in list
                discussions: state.discussions.map((d) =>
                    d.id === discussionId
                        ? { ...d, comment_count: comments.length }
                        : d
                ),
            };
        });
    },

    reset: () => set(initialState),
}));
